import { useState, type ReactNode } from "react";
import { QueryStatus, useQuery } from "@tanstack/react-query";
import { Bell, ChevronDown, ChevronUp } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { LockedContent } from "@/components/layout/locked-content";
import { GoogleIcon } from "@/components/icons/google";
import { MetaIcon } from "@/components/icons/meta";
import { LinkedinIcon } from "@/components/icons/linkedin";
import { TwitterIcon } from "@/components/icons/twitter";

import { cn, message, sanitizeUrl } from "@/lib/utils";
import { getAlertSeverityClass } from "@/lib/chart";
import { BrandGovernanceApiFactory, type LiveMonitorParams } from "@/apis/brand-os/brand-governance";
import { LiveMonitorAlert, LiveMonitorAlertCreative, LiveMonitorData } from "@/schema/brand-os/brand-governance";

function getPlatformIcon(platform: string): ReactNode {
  switch (platform) {
    case "Google Ads":
      return <GoogleIcon className="size-6" />;
    case "Facebook Ads":
    case "Meta Ads":
      return <MetaIcon className="size-6" />;
    case "Twitter Ads":
      return <TwitterIcon className="size-6" />;
    case "Linkedin Ads":
      return <LinkedinIcon className="size-6" />;
    default:
      return null;
  }
}

function ActiveAlertsComponent({ status, data }: { status: QueryStatus; data?: LiveMonitorData | null }) {
  switch (status) {
    case "pending":
      return <ActiveAlertsSkeleton />;
    case "error":
      return <LockedContent message={message} />;
    case "success":
      return data ? <ActiveAlertsContent data={data} /> : <LockedContent message={message} />;
  }
}

export function ActiveAlerts({ params }: { params: LiveMonitorParams }) {
  const { data, status } = useQuery(BrandGovernanceApiFactory.Queries.LiveMonitor(params));
  const alertCount = data?.data?.alerts?.length ?? 0;

  return (
    <Card>
      <CardHeader className="flex items-center gap-2">
        <div className="flex size-8 items-center justify-center rounded-lg bg-destructive/10">
          <Bell className="size-4 text-destructive" />
        </div>
        <CardTitle>Active Alerts {alertCount > 0 ? <span className="text-xs font-normal text-muted-foreground">({alertCount})</span> : null}</CardTitle>
      </CardHeader>
      <CardContent>
        <ActiveAlertsComponent status={status} data={data?.data} />
      </CardContent>
    </Card>
  );
}

function ActiveAlertsContent({ data }: { data: LiveMonitorData }) {
  const alerts = data.alerts ?? [];

  if (!alerts.length) {
    return <LockedContent message={message} />;
  }

  return (
    <div className="flex max-h-110 flex-col gap-2 overflow-y-auto scrollbar-hidden">
      {alerts.map((alert, index) => (
        <AlertRow key={`${alert.dimension}-${alert.sub_brand}-${index}`} alert={alert} />
      ))}
    </div>
  );
}

function CreativePreview({ url, format, alt }: { url: string; format: string; alt: string }) {
  const safeUrl = sanitizeUrl(url);
  const isVideo = format.toLowerCase().includes("video");

  if (!safeUrl) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <p className="text-xs text-muted-foreground">No preview</p>
      </div>
    );
  }

  if (isVideo) {
    return <video src={safeUrl} className="h-full w-full object-contain bg-secondary rounded" muted playsInline controls />;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger className="h-full w-full">
          <img src={safeUrl} alt={alt} className="h-full w-full object-contain bg-secondary rounded" />
        </TooltipTrigger>
        <TooltipContent side="right" className="">
          <img src={safeUrl} alt={alt} className="size-96 object-contain bg-secondary rounded" />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function CreativeDetail({ creative }: { creative: LiveMonitorAlertCreative }) {
  return (
    <li className="flex flex-col lg:flex-row items-center lg:items-start gap-5 py-6 border-t border-border/50 first:border-0 first:pt-0">
      <div className="max-w-48 h-full overflow-hidden rounded">
        <CreativePreview url={creative.url} format={creative.format} alt={creative.sub_brand} />
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-1">
          <div className="flex gap-2 items-center">
            {getPlatformIcon(creative.platform)}
            <span className="text-xs font-medium text-foreground">
              {creative.platform} · <span className="text-muted-foreground/70">{creative.format}</span>
            </span>
          </div>
          <span className="text-xs text-muted-foreground">
            Score: <span className="font-medium text-foreground">{creative.dimension_score.toFixed(1)}</span>
          </span>
        </div>

        <p className="text-xs text-muted-foreground leading-relaxed">{creative.summary}</p>

        {creative.negatives.length ? (
          <ul className="mt-1 list-disc space-y-1 pl-4">
            {creative.negatives.map((neg, i) => (
              <li key={i} className="text-xs text-destructive">{neg}</li>
            ))}
          </ul>
        ) : null}
      </div>
    </li>
  );
}

function AlertRow({ alert }: { alert: LiveMonitorAlert }) {
  const [expanded, setExpanded] = useState(false);
  const subtitle = [alert.sub_brand, alert.platform].filter(Boolean).join(" · ");
  const meta = alert.count > 0 ? `${alert.count} creative${alert.count !== 1 ? "s" : ""}` : null;

  return (
    <button
      type="button"
      className={cn("w-full text-left rounded-lg transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", "p-0 border-0 cursor-pointer")}
      onClick={() => setExpanded((prev) => !prev)}
      tabIndex={0}
    >
      <div className="flex items-center justify-between gap-6 py-2.5 px-2">
        <Badge className={cn("shrink-0 rounded-full text-xs font-medium text-center capitalize min-w-24", getAlertSeverityClass(alert.severity))}>{alert.severity}</Badge>
        <div className="flex-1">
          <p className="text-sm text-foreground">{alert.title}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">{[subtitle, meta].filter(Boolean).join(" · ") || "—"}</p>
        </div>
        <div className="shrink-0">{expanded ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}</div>
      </div>
      {expanded && alert.creatives.length ? (
        <div className="px-6 pb-3">
          <ul className="flex flex-col mt-5">
            {alert.creatives.map((creative, i) => (
              <CreativeDetail key={i} creative={creative} />
            ))}
          </ul>
        </div>
      ) : null}
    </button>
  );
}


function ActiveAlertsSkeleton() {
  return (
    <div className="flex flex-col gap-2">
      {Array.from({ length: 3 }, (_, i) => (
        <div key={i} className="flex w-full items-center gap-4 rounded-lg px-1 py-2">
          <Skeleton className="h-6 w-24 shrink-0 rounded-full" />
          <div className="min-w-0 flex-1 space-y-1">
            <Skeleton className="h-4 w-full max-w-md rounded-md" />
            <Skeleton className="h-3 w-32 rounded-md" />
          </div>
          <Skeleton className="size-5 shrink-0 rounded" />
        </div>
      ))}
    </div>
  );
}
